'use client';

import Link from '@carvana/ds-link';
import { icon } from '@carvana/ds-styles';
import { MapPin, Phone } from 'lucide-react';
import { Fragment } from 'react';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import { testDriveCenterImages } from '~/constants/dealership/testDriveCenter/assets';
import {
  useDealershipAddress,
  useDealershipConfiguration,
  useDealershipDepartmentHours,
  useDealershipPhones
} from '~/context/dealership/StaticContext';
import optimizeImage from '~/utilities/optimizeImage';

const section = 'Test Drive Center - Visit';

const dayNames: Record<string, string> = {
  fri: 'Friday',
  mon: 'Monday',
  sat: 'Saturday',
  sun: 'Sunday',
  thu: 'Thursday',
  thur: 'Thursday',
  thurs: 'Thursday',
  tue: 'Tuesday',
  tues: 'Tuesday',
  wed: 'Wednesday'
};

// The CMS hour strings vary in format ("Mon – Fri", "8:30a – 7:00p",
// "8:30 a.m. - 7:00 p.m."); the design shows them as "Monday - Friday" and
// "8:30am - 7pm", so normalize at display time without touching the data
// other sections consume.
function expandDays(days: string) {
  return days
    .replace(/[A-Za-z]+/g, word => dayNames[word.toLowerCase()] ?? word)
    .replace(/\s*[-–—]\s*/g, ' - ');
}

function compactHours(hours: string) {
  return hours
    .replace(/(\d):00(?!\d)/g, '$1')
    .replace(
      /(\d)\s*([AaPp])(?:\.?\s?[Mm]\.?)?(?=\W|$)/g,
      (_, digit, meridiem) => `${digit}${meridiem.toLowerCase()}m`
    )
    .replace(/\s*[-–—]\s*/g, ' - ');
}

export default function TestDriveCenterVisit() {
  const configuration = useDealershipConfiguration();
  const dealershipAddress = useDealershipAddress();
  const hours = useDealershipDepartmentHours();
  const phones = useDealershipPhones();

  const dealershipName = configuration.fullName ?? configuration.name ?? '';
  const addressLine = [
    [dealershipAddress.street1, dealershipAddress.street2]
      .filter(Boolean)
      .join(', '),
    [dealershipAddress.city, dealershipAddress.state]
      .filter(Boolean)
      .join(', '),
    dealershipAddress.zip
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="bg-canvas-default px-2xl py-6xl md:px-3xl md:py-7xl lg:px-4xl">
      <div className="mx-auto flex max-w-[1272px] flex-col gap-4xl rounded-2xl bg-background-medium p-2xl text-text-inverse md:flex-row md:items-center md:gap-6xl md:p-7xl">
        <div className="md:w-1/2">
          <img
            src={optimizeImage(testDriveCenterImages.storefrontLot, {
              crop: '3:2',
              format: 'auto',
              optimize: 'medium'
            })}
            alt={`The ${dealershipName} Test Drive Center`}
            width={544}
            height={363}
            className="w-full rounded-xl object-cover"
            loading="lazy"
          />
        </div>

        <div className="flex flex-col gap-y-4xl md:w-1/2">
          <div className="flex flex-col gap-y-xl">
            <h2 className="t-heading-xl md:t-heading-2xl">
              {dealershipName || 'Come see it in person'}
            </h2>
            <div className="flex flex-col gap-y-xs">
              {addressLine && (
                <div className="flex items-center gap-x-md">
                  <MapPin
                    className="flex-none"
                    color={icon.inverse}
                    size={16}
                  />
                  <p className="t-body-sm">{addressLine}</p>
                </div>
              )}
              {phones.length > 0 && (
                <div className="flex items-start gap-x-md">
                  <Phone
                    className="mt-xs flex-none"
                    color={icon.inverse}
                    size={16}
                  />
                  <p className="flex flex-wrap items-center gap-x-lg t-body-sm">
                    {phones.map((phone, index) => (
                      <Fragment key={phone.label}>
                        {index > 0 && (
                          <span
                            aria-hidden
                            className="h-[16px] w-[1px] bg-border-inverse-transparent opacity-40"
                          />
                        )}
                        <span>
                          <span className="t-label-sm">{phone.label}: </span>
                          <Link
                            href={`tel:${phone.number}`}
                            size="sm"
                            variant="standalone"
                            className="text-text-inverse"
                            preventVisitedColor
                            data-adobe-id={`click-to-call-${phone.label.toLowerCase()}`}
                            data-analytics-id={`${section} - Phone ${phone.label}`}
                            data-sda-event={ShiftDigitalEventName.ClickToCall}
                            data-sda-event-set-prop={`clickToCallDepartment|${phone.label}`}
                            data-testid={`test-drive-center-visit-phone-${phone.label.toLowerCase()}`}
                          >
                            {phone.number}
                          </Link>
                        </span>
                      </Fragment>
                    ))}
                  </p>
                </div>
              )}
            </div>
          </div>

          {hours.length > 0 && (
            <div className="flex flex-col gap-y-lg">
              <h3 className="t-heading-sm">Hours of operation</h3>
              <div className="flex flex-col gap-y-2xl sm:flex-row sm:gap-x-4xl">
                {hours.map(department => (
                  <div
                    key={department.label}
                    className="flex flex-1 flex-col gap-y-md"
                  >
                    <div className="text-text-inverse-subtle t-label-sm">
                      {department.label}
                    </div>
                    <div className="flex flex-col gap-y-sm">
                      {department.daysHours.map(row => (
                        <div
                          key={row.days}
                          className="flex justify-between gap-x-lg t-body-sm"
                        >
                          <span>{expandDays(row.days)}</span>
                          <span>{compactHours(row.hours)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
